// @ts-check
/**
 * THE STUDIO'S OWN CIVIL DAY, America/Chicago, independent of whoever is
 * asking.
 *
 * A browser reads a Timestamp in the viewer's own local time zone. A Netlify
 * function has no viewer and used to fall back to UTC, which is wrong for a
 * Central studio: Central is UTC-5 or UTC-6, so the UTC day rolls over at
 * 7pm (6pm in winter) Central, in the middle of a teaching evening. A
 * reinstatement stamped at 8:05pm Central used to read as the NEXT day on
 * the server and the SAME day in the browser, and the reinstatement baseline
 * is EXCLUSIVE (a later baseline forgives more), so the server silently
 * forgave one extra class the browser still counted and a real concern
 * vanished from Kim's list under a valid "Scanned at" stamp.
 *
 * The fix is not "make the server guess a viewer's zone" (it has none) and
 * not "make the browser use its own local zone" (a family's phone can be set
 * to any zone). The day that matters is the STUDIO'S, which is a constant:
 * CLAUDE.md, "Every time shown to Bobbie is STUDIO TIME, America/Chicago."
 * So every reader of a reinstatement timestamp, client or server, reads the
 * SAME civil day through THIS ONE function.
 *
 * This file has no imports so a Netlify function bundle can pull it in
 * directly, and hub/tsconfig.json's allowImportingTsExtensions note (about
 * netlify/functions/lib/practiceBadges.mjs) is why the client is allowed to
 * import it too, the same way, rather than keeping a second copy of this
 * math: hub/src/lib/offerings.ts imports centralCivilDateFromTimestamp from
 * here instead of reading the browser's own clock fields.
 */

/**
 * The America/Chicago civil date parts for a real instant.
 * @param {Date} date
 * @returns {{ year: number, month: number, day: number }}
 */
export function centralCivilDateParts(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return { year: Number(map.year), month: Number(map.month), day: Number(map.day) };
}

/**
 * 'YYYY-MM-DD' for a real instant, read in America/Chicago.
 * @param {Date} date
 * @returns {string}
 */
export function centralCivilDateKey(date) {
  const { year, month, day } = centralCivilDateParts(date);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * 'YYYY-MM-DD' for a Firestore Timestamp (Admin SDK or client SDK; anything
 * with a `.toDate()` method), read in America/Chicago. Null for anything
 * that is not really a Timestamp, so a bare Date or an epoch number reads as
 * "no baseline" (the full lookback) rather than a wrong day, on both sides.
 * @param {unknown} value
 * @returns {string | null}
 */
export function centralCivilDateFromTimestamp(value) {
  const ts = /** @type {{ toDate?: () => Date } | null | undefined} */ (value);
  if (!ts || typeof ts !== 'object' || typeof ts.toDate !== 'function') return null;
  try {
    const d = ts.toDate();
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
    return centralCivilDateKey(d);
  } catch {
    return null;
  }
}
